import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import Layout from '@/components/Layout';
import UnsplashImagePicker from '@/components/UnsplashImagePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AreaEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null); // 'image_1' or 'image_2'
  
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    additional_details: '',
    order: 0,
    enabled: true,
    image_1_link: '',
    image_2_link: '',
    image_1_url: '', // URL of image 1
    image_2_url: '',  // URL of image 2
    icon_image_url: '' // Main icon image for homepage
  });

  useEffect(() => {
    loadArea();
  }, [id]);

  const loadArea = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/areas/${id}`, {
        withCredentials: true
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load area');
      navigate('/admin/areas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/admin/areas/${id}`, formData, {
        withCredentials: true
      });
      toast.success('Area updated successfully!');
      navigate('/admin/areas');
    } catch (error) {
      let errorMessage = 'Failed to update area';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        }
      }
      toast.error(errorMessage);
    }
  };

  const openUnsplashPicker = (imageField) => {
    setCurrentImageField(imageField);
    setShowUnsplashPicker(true);
  };

  const handleUnsplashSelect = (imageData) => {
    if (currentImageField === 'image_1') {
      setFormData(prev => ({ ...prev, image_1_url: imageData.url }));
    } else if (currentImageField === 'image_2') {
      setFormData(prev => ({ ...prev, image_2_url: imageData.url }));
    } else if (currentImageField === 'icon_image') {
      setFormData(prev => ({ ...prev, icon_image_url: imageData.url }));
    }
    toast.success(`Image by ${imageData.photographer} selected!`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin/areas')}
          className="retro-button-small mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Areas
        </button>

        <div className="cassette-card p-8">
          <h1 className="text-3xl retro-title mb-6">Edit Area of Focus</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Slug (URL)</label>
                <input
                  type="text"
                  value={formData.slug}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-gray-200 text-[#2C1810]"
                  disabled
                />
                <p className="text-xs text-[#6B4423] mt-1">Cannot change slug when editing</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Display Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Subtitle</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Additional Details</label>
              <textarea
                value={formData.additional_details}
                onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            {/* Main Icon Image - Shows on Homepage */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Homepage Icon Image *</label>
              <p className="text-xs text-[#6B4423] mb-2">This image will be displayed on the main page</p>
              <button
                type="button"
                onClick={() => openUnsplashPicker('icon_image')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.icon_image_url ? 'Replace Homepage Icon' : 'Browse Images from Unsplash'}
              </button>
              {formData.icon_image_url && (
                <div className="mt-3">
                  <img
                    src={formData.icon_image_url}
                    alt="Icon preview"
                    className="h-32 w-32 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Homepage icon image</p>
                </div>
              )}
            </div>

            {/* Image 1 */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 1</label>
              <button
                type="button"
                onClick={() => openUnsplashPicker('image_1')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.image_1_url ? 'Replace Image 1 from Unsplash' : 'Browse Images from Unsplash'}
              </button>
              {formData.image_1_url && (
                <div className="mt-3">
                  <img
                    src={formData.image_1_url}
                    alt="Image 1 preview"
                    className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Selected image 1</p>
                </div>
              )}
            </div>

            {/* Image 1 Link */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 1 Link (Optional)</label>
              <input
                type="url"
                value={formData.image_1_link}
                onChange={(e) => setFormData({ ...formData, image_1_link: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                placeholder="https://example.com"
              />
              <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 1, they'll be taken to this URL</p>
            </div>

            {/* Image 2 */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 2</label>
              <button
                type="button"
                onClick={() => openUnsplashPicker('image_2')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.image_2_url ? 'Replace Image 2 from Unsplash' : 'Browse Images from Unsplash'}
              </button>
              {formData.image_2_url && (
                <div className="mt-3">
                  <img
                    src={formData.image_2_url}
                    alt="Image 2 preview"
                    className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Selected image 2</p>
                </div>
              )}
            </div>

            {/* Image 2 Link */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 2 Link (Optional)</label>
              <input
                type="url"
                value={formData.image_2_link}
                onChange={(e) => setFormData({ ...formData, image_2_link: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                placeholder="https://example.com"
              />
              <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 2, they'll be taken to this URL</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5"
              />
              <label htmlFor="enabled" className="text-sm font-semibold text-[#6B4423]">
                Enable (Show on website)
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="retro-button flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/areas')}
                className="retro-button bg-gray-500 hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Unsplash Image Picker Modal */}
        <UnsplashImagePicker
          isOpen={showUnsplashPicker}
          onClose={() => {
            setShowUnsplashPicker(false);
            setCurrentImageField(null);
          }}
          onSelect={handleUnsplashSelect}
        />
      </div>
    </Layout>
  );
}
